import React from 'react';
import {
  Trash2, Eye, EyeOff, Fullscreen, Square, Minus, Circle, Type, Pyramid, HelpCircle, Link, GripVertical, Grid2x2, Image // Aggiunte Grid2x2 e Image
} from 'lucide-react';
import type { LayerBase } from '../../../store/types';
import useGeoData from '../../../store/useGeoData';
import { useShallow } from 'zustand/react/shallow';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Stile comune per le icone dei layer
const layerIconProps = { size: 16, style: { marginRight: 'var(--spacing-s)', flexShrink: 0 } }; // Ridotto margine dx
// Stile per l'handle di trascinamento
const dragHandleProps = { size: 16, style: { marginRight: 'var(--spacing-s)', cursor: 'grab', flexShrink: 0 } };

// Funzione per ottenere l'icona specifica per il tipo di layer (escluso WMS)
const getLayerIcon = (layerType: Exclude<LayerBase['maplibreLayerType'], 'wms'>) => {
  switch (layerType) {
    case 'fill': return <Square {...layerIconProps} />;
    case 'line': return <Minus {...layerIconProps} />;
    case 'circle': return <Circle {...layerIconProps} />;
    case 'symbol': return <Type {...layerIconProps} />;
    case 'fill-extrusion': return <Pyramid {...layerIconProps} />;
    case 'raster': // Assumiamo che 'raster' (non WMS) usi HelpCircle per ora
    case 'heatmap': // Assumiamo che 'heatmap' usi HelpCircle per ora
    default: return <HelpCircle {...layerIconProps} />;
  }
};

// --- Componente SortableLayerItem ---
interface SortableLayerItemProps {
  layer: LayerBase;
  selectedLayerId: string | null;
  onLayerClick: (layerId: string) => void;
  onToggleVisibility: (layerId: string) => void;
  onRemoveLayer: (layerId: string) => void;
  onZoomToLayer: (layerId: string) => void;
}

const SortableLayerItem: React.FC<SortableLayerItemProps> = ({
  layer,
  selectedLayerId,
  onLayerClick,
  onToggleVisibility,
  onRemoveLayer,
  onZoomToLayer,
}) => {
  // Recupera gli stili dei layer dallo store
  const layerStyles = useGeoData(useShallow((state) => state.layerStyles));
  const currentStyle = layerStyles[layer.id];

  // Determina il colore principale per l'icona
  // Priorità: fillColor, lineColor, circleColor (nomi corretti delle proprietà)
  const iconColor = currentStyle?.fillColor ||
                    currentStyle?.lineColor ||
                    currentStyle?.circleColor ||
                    undefined; // Colore di default o undefined se nessuno è specificato

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging, // Possiamo usare isDragging per stili aggiuntivi
  } = useSortable({ id: layer.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1, // Esempio: riduci opacità durante il drag
    zIndex: isDragging ? 10 : 'auto', // Assicura che l'elemento trascinato sia sopra gli altri
    position: 'relative', // Necessario per zIndex
    // Stili precedenti mantenuti e combinati
    marginBottom: 'var(--spacing-s)',
    fontSize: 'var(--font-size-base)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center', // Rimosso !important, dovrebbe funzionare
    width: '100%',
    boxSizing: 'border-box',
    padding: 'var(--spacing-xs) var(--spacing-m)',
    cursor: 'pointer',
    borderRadius: 'var(--border-radius-small)',
    backgroundColor: layer.id === selectedLayerId ? 'var(--color-background-selected)' : 'transparent',
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      onClick={() => onLayerClick(layer.id)}
      onMouseEnter={(e) => {
        if (layer.id !== selectedLayerId) {
          e.currentTarget.style.backgroundColor = 'var(--color-background-hover)';
        }
      }}
      onMouseLeave={(e) => {
        if (layer.id !== selectedLayerId) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
      {...attributes} // Passa attributi D&D (ma non i listeners qui, li mettiamo sull'handle)
    >
      {/* Handle per Drag & Drop */}
      <span {...listeners} style={{ display: 'flex', alignItems: 'center' }} title="Trascina per riordinare">
        <GripVertical {...dragHandleProps} />
      </span>

      {/* Icona Tipo Layer - Passa il colore determinato */}
      {layer.format === 'wms' ? (
        <span title="Layer WMS" style={{ display: 'flex', alignItems: 'center' }}><Link {...layerIconProps} /></span>
      ) : // Controlla sourceSpecification per layer da URL/TileJSON
      layer.sourceSpecification &&
      (layer.sourceSpecification.type === 'vector' || layer.sourceSpecification.type === 'geojson') &&
      (('url' in layer.sourceSpecification && layer.sourceSpecification.url) ||
       ('tiles' in layer.sourceSpecification && Array.isArray(layer.sourceSpecification.tiles) && layer.sourceSpecification.tiles.length > 0) ||
       (layer.sourceSpecification.type === 'geojson' && typeof layer.sourceSpecification.data === 'string' && layer.sourceSpecification.data))
      ? (
        <span title={`Layer Vettoriale (${layer.sourceSpecification.type}) da Sorgente Dati`} style={{ display: 'flex', alignItems: 'center' }}><Grid2x2 {...layerIconProps} color={typeof iconColor === 'string' ? iconColor : undefined} /></span>
      ) : layer.sourceSpecification &&
        layer.sourceSpecification.type === 'raster' &&
        (('url' in layer.sourceSpecification && layer.sourceSpecification.url) ||
         ('tiles' in layer.sourceSpecification && Array.isArray(layer.sourceSpecification.tiles) && layer.sourceSpecification.tiles.length > 0))
        ? (
          <span title="Layer Raster da Sorgente Dati" style={{ display: 'flex', alignItems: 'center' }}><Image {...layerIconProps} color={typeof iconColor === 'string' ? iconColor : undefined} /></span>
        ) : (
        // Altri layer (locali, o tipi non gestiti specificamente sopra come URL, o sourceSpecification mancante)
        (() => {
          const type = layer.maplibreLayerType;
          if (typeof type === 'string' && type !== 'wms') {
            return React.cloneElement(
              getLayerIcon(type as Exclude<LayerBase['maplibreLayerType'], 'wms'>),
              { color: iconColor }
            );
          }
          // Fallback con title corretto
          return <span title="Tipo layer non gestito o sconosciuto"><HelpCircle {...layerIconProps} /></span>;
        })()
      )}

      {/* Nome Layer */}
      <span style={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 'var(--spacing-m)' }} title={layer.name}>
        {layer.name}
      </span>

      {/* Contenitore Icone Azioni */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-m)', flexShrink: 0 }}>
        {/* Icona Zoom */}
        <span
          title="Zoom all'estensione del Layer"
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, color: 'inherit' }}
          onClick={(e) => {
            e.stopPropagation();
            onZoomToLayer(layer.id);
          }}
        >
          <Fullscreen size={16} />
        </span>
        {/* Icona Visibilità */}
        <span
          title={layer.visible ? "Nascondi Layer" : "Mostra Layer"}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(layer.id);
          }}
        >
          {layer.visible ? <Eye size={16} /> : <EyeOff size={16} />}
        </span>
        {/* Icona Elimina */}
        <span title="Rimuovi Layer" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <Trash2
            size={16}
            style={{ color: 'var(--color-text-danger)' }}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveLayer(layer.id);
            }}
          />
        </span>
      </div>
    </li>
  );
};

// --- Componente LayerList ---
interface LayerListProps {
  layers: LayerBase[];
  onRemoveLayer: (layerId: string) => void;
  onToggleVisibility: (layerId: string) => void;
}

const LayerList: React.FC<LayerListProps> = ({
  layers,
  onRemoveLayer,
  onToggleVisibility,
}) => {
  // Ottieni stato e azioni dallo store, inclusa la nuova azione reorderLayers
  const { selectedLayerId, setSelectedLayerId, zoomToLayerExtent, reorderLayers } = useGeoData(
    useShallow((state) => ({
      selectedLayerId: state.selectedLayerId,
      setSelectedLayerId: state.setSelectedLayerId,
      zoomToLayerExtent: state.zoomToLayerExtent,
      reorderLayers: state.reorderLayers, // Aggiunta azione di riordino
    }))
  );

  // Configura sensori per D&D (Pointer per mouse/touch, Keyboard per accessibilità)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handler per il click sull'elemento della lista
  const handleLayerClick = (layerId: string) => {
    setSelectedLayerId(selectedLayerId === layerId ? null : layerId);
  };

  // Handler per la fine del trascinamento
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = layers.findIndex((layer) => layer.id === active.id);
      const newIndex = layers.findIndex((layer) => layer.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderLayers(oldIndex, newIndex); // Chiama l'azione dello store per riordinare
      } else {
        console.error("Errore nel trovare gli indici per il riordino:", active.id, over.id, oldIndex, newIndex);
      }
    }
  };

  // Estrai gli ID dei layer per SortableContext
  const layerIds = layers.map(layer => layer.id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={layerIds} // Passa gli ID dei layer
        strategy={verticalListSortingStrategy}
      >
        <ul style={{ listStyle: 'none', paddingLeft: '0', marginTop: '0px', width: '100%', boxSizing: 'border-box' }}>
          {layers.length === 0 ? (
            <li style={{ fontSize: 'var(--font-size-small)', color: 'var(--color-text-secondary)', paddingLeft: 'var(--spacing-m)' }}>
              Nessun layer importato
            </li>
          ) : (
            layers.map(layer => (
              <SortableLayerItem
                key={layer.id}
                layer={layer}
                selectedLayerId={selectedLayerId}
                onLayerClick={handleLayerClick}
                onToggleVisibility={onToggleVisibility}
                onRemoveLayer={onRemoveLayer}
                onZoomToLayer={zoomToLayerExtent} // Passa la funzione zoom
              />
            ))
          )}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

export default LayerList;